import { notEqual } from '@/api/helpers';
import { DashboardFilterType, DashboardFilterValue } from '@/client/reports';
import {
  BranchesMultiselectInput,
  GroupsMultiselectInput,
} from '@/components/form/selectors';
import { DepartmentsMultiselectInput } from '@/components/form/selectors/DepartmentsMultiselectInput';
import { ManagersMultiselectInput } from '@/components/form/selectors/ManagersMultiselectInput';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { filterTypeOptions } from '@/utils/helpers';
import { Form, Formik } from 'formik';
import { Dialog, DialogProps } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as yup from 'yup';

const StyledDialog = styled(Dialog)`
  width: 500px;
`;

type FilterSelectorProps = {
  onSubmit: (data: DashboardFilterValue) => void;
  accountId?: string;
  initialFilter?: DashboardFilterValue;
} & DialogProps;

export const FilterSelector: React.FC<FilterSelectorProps> = ({
  onSubmit,
  onHide,
  visible,
  accountId,
  initialFilter,
  ...dialogProps
}) => {
  const { t } = useTranslation();

  const validationSchema = yup.object().shape({
    type: yup.string().required(),
    managers: yup.array(),
    departments: yup.array(),
    branches: yup.array(),
    groups: yup.array(),
  });

  return (
    <StyledDialog
      blockScroll
      visible={visible}
      header={<h1>{t('filter.selectFilters')}</h1>}
      onHide={onHide}
      draggable={false}
      data-testid="filter-selector-modal"
      {...dialogProps}
    >
      <Formik
        key={`formik-${JSON.stringify(initialFilter)}`}
        initialValues={
          initialFilter || {
            type: DashboardFilterType.ALL_DATA,
            managers: [],
            departments: [],
            branches: [],
            groups: [],
          }
        }
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={(values) => {
          const cleanedValues: DashboardFilterValue = {
            type: values.type,
            managers:
              values.type === DashboardFilterType.MANAGERS
                ? values.managers
                : [],
            departments:
              values.type === DashboardFilterType.DEPARTMENTS
                ? values.departments
                : [],
            branches:
              values.type === DashboardFilterType.BRANCHES
                ? values.branches
                : [],
            groups:
              values.type === DashboardFilterType.GROUPS ? values.groups : [],
          };
          onSubmit(cleanedValues);
        }}
      >
        {({ setFieldValue, values, errors }) => {
          const handleFilterTypeChange = (newType: DashboardFilterType) => {
            if (values.type !== newType) {
              setFieldValue('type', newType);
              setFieldValue('managers', []);
              setFieldValue('departments', []);
              setFieldValue('branches', []);
              setFieldValue('groups', []);
            }
          };

          return (
            <Form>
              <div className="field w-full mb-4">
                <label htmlFor="filterType">{t('filter.filterBy')}:</label>
                <Dropdown
                  id="filterType"
                  value={values.type}
                  options={filterTypeOptions(t)}
                  onChange={(e) =>
                    handleFilterTypeChange(
                      e.value || DashboardFilterType.ALL_DATA,
                    )
                  }
                  placeholder={t('filter.selectFilterType')}
                  className="w-full"
                  clearIcon="pi pi-times"
                  showClear
                />
              </div>

              {values.type === DashboardFilterType.MANAGERS && (
                <div className="field w-full">
                  <label htmlFor="managers">
                    {`${t('filter.managers')} (${
                      values.managers?.length ?? 0
                    })`}
                  </label>
                  <ManagersMultiselectInput
                    id="managers"
                    className="w-full"
                    onChange={(managers) => setFieldValue('managers', managers)}
                    selectedOptions={values.managers}
                    accountId={accountId}
                  />
                </div>
              )}

              {values.type === DashboardFilterType.DEPARTMENTS && (
                <div className="field w-full">
                  <label htmlFor="departments">
                    {`${t('filter.departments')} (${
                      values.departments?.length ?? 0
                    })`}
                  </label>
                  <DepartmentsMultiselectInput
                    id="departments"
                    className="w-full"
                    onChange={(departments) =>
                      setFieldValue('departments', departments)
                    }
                    selectedOptions={values.departments}
                    accountId={accountId}
                  />
                </div>
              )}

              {values.type === DashboardFilterType.BRANCHES && (
                <div className="field w-full">
                  <label htmlFor="branches">
                    {`${t('filter.branches')} (${
                      values.branches?.length ?? 0
                    })`}
                  </label>
                  <BranchesMultiselectInput
                    className="w-full"
                    onChange={(branches) => setFieldValue('branches', branches)}
                    selectedOptions={values.branches}
                    accountId={accountId}
                  />
                </div>
              )}

              {values.type === DashboardFilterType.GROUPS && (
                <div className="field w-full">
                  <label htmlFor="groups">
                    {`${t('filter.groups')} (${values.groups?.length ?? 0})`}
                  </label>
                  <GroupsMultiselectInput
                    className="w-full"
                    onChange={(groups) => setFieldValue('groups', groups)}
                    selectedOptions={values.groups}
                    accountId={accountId}
                    additionalFilters={[notEqual('isForPhishing', true)]}
                  />
                </div>
              )}

              <FlexContainer justify="flex-end" className="mt-5">
                <AppButton
                  label={t('button.cancel')}
                  severity="secondary"
                  type="outlined"
                  onClick={onHide}
                  className="mr-3"
                  data-testid="filter-selector-cancel"
                />
                <AppButton
                  label={t('button.apply')}
                  isSubmit
                  isDisabled={!!Object.keys(errors).length}
                  data-testid="filter-selector-apply"
                />
              </FlexContainer>
            </Form>
          );
        }}
      </Formik>
    </StyledDialog>
  );
};
